import { useRef, useEffect, useState } from "react";
import { useLanguage } from "../context/LanguageContext";
import { Award, Zap, Globe, Lock, Clock, HeartHandshake, Lightbulb } from "lucide-react";

const ICONS = [Award, Zap, Globe, Lock, Clock, HeartHandshake, Lightbulb];

export default function AboutUs() {
    const { t } = useLanguage();
    const sectionRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) setIsVisible(true);
            },
            { threshold: 0.1 }
        );
        const current = sectionRef.current;
        if (current) observer.observe(current);
        return () => {
            if (current) observer.unobserve(current);
        };
    }, []);

    return (
        <section
            id="sobre-nosotros"
            className="py-20 md:py-32 px-6 bg-slate-50 dark:bg-slate-900/50"
            ref={sectionRef}
            data-testid="about-section"
        >
            <div className="max-w-7xl mx-auto">
                <div
                    className="text-center mb-16"
                    style={{
                        opacity: isVisible ? 1 : 0,
                        transform: isVisible ? "translateY(0)" : "translateY(24px)",
                        transition: "opacity 0.6s ease, transform 0.6s ease",
                    }}
                >
                    <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">
                        {t.about.title}
                    </h2>
                    <p className="mt-4 text-base md:text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto">
                        {t.about.subtitle}
                    </p>
                    <p className="mt-4 text-base text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
                        {t.about.description}
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                    {t.about.values.map((value, i) => {
                        const Icon = ICONS[i] || Award;
                        return (
                            <div
                                key={i}
                                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 hover:border-blue-400/50 dark:hover:border-blue-500/50 transition-all duration-300 hover:shadow-md hover:-translate-y-1"
                                data-testid={`value-${i}`}
                                style={{
                                    opacity: isVisible ? 1 : 0,
                                    transform: isVisible ? "translateY(0)" : "translateY(24px)",
                                    transition: `opacity 0.5s ease ${i * 0.08}s, transform 0.5s ease ${i * 0.08}s`,
                                }}
                            >
                                <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center mb-4">
                                    <Icon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                </div>
                                <h3 className="font-semibold mb-2 text-sm">{value.title}</h3>
                                <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                                    {value.desc}
                                </p>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
